'use client'

import { useEffect, useRef, type ReactNode } from 'react'

import { usePathname } from 'next/navigation'

import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'

import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum'

// Referências estáticas — Next só injeta NEXT_PUBLIC_* no bundle assim (não usar process.env[`NEXT_PUBLIC_${name}`]).
const datadogConfig = {
  applicationId: process.env.NEXT_PUBLIC_DD_APPLICATION_ID,
  clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
  site: process.env.NEXT_PUBLIC_DD_SITE || 'us3.datadoghq.com',
  service: process.env.NEXT_PUBLIC_DD_SERVICE || 'only-view-web',
  env: process.env.NEXT_PUBLIC_DD_ENV || process.env.NODE_ENV || 'development',
  version: process.env.NEXT_PUBLIC_DD_VERSION,
}

let isRumInitialized = false
let isHttpTrackingInstalled = false

type DatadogRequestConfig = InternalAxiosRequestConfig & {
  metadata?: { ddStartTime?: number }
}

export type DatadogUserInput = {
  id?: string | number
  userUuid?: string | number
  idConta?: string | number
  idConductor?: string | number
  nome?: string
  name?: string
  email?: string
}

function parseSampleRate(value: string | undefined, fallback: number) {
  if (!value) return fallback
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function isDatadogEnabled() {
  const value = process.env.NEXT_PUBLIC_DD_ENABLED
  if (value === undefined || value === '') return true
  return !['false', '0', 'off', 'no'].includes(
    String(value).trim().toLowerCase(),
  )
}

function getAllowedTracingUrls(): string[] {
  const urls = [process.env.NEXT_PUBLIC_API_BACKEND_URL].filter(Boolean)
  const origins = new Set<string>()
  urls.forEach((url) => {
    try {
      origins.add(new URL(url as string).origin)
    } catch {
      // ignore invalid URL
    }
  })
  return Array.from(origins)
}

export function isDatadogRumActive() {
  return isRumInitialized
}

function mapUserToDatadog(user: DatadogUserInput) {
  const id = user.userUuid ?? user.idConta ?? user.id ?? user.idConductor
  if (id === undefined || id === null || id === '') return null

  const mapped: { id: string; name?: string; email?: string } = {
    id: String(id),
  }
  const name = user.nome || user.name
  if (name) mapped.name = String(name)
  if (user.email) mapped.email = String(user.email)

  return mapped
}

export function identifyDatadogUser(user: DatadogUserInput) {
  if (!isRumInitialized) return

  const mapped = mapUserToDatadog(user)
  if (!mapped) return

  datadogRum.setUser(mapped)
}

export function clearDatadogUser() {
  if (!isRumInitialized) return
  datadogRum.clearUser()
}

function toError(value: unknown): Error {
  if (value instanceof Error) return value
  if (typeof value === 'string') return new Error(value)
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return new Error(String(value))
  }
  try {
    return new Error(JSON.stringify(value))
  } catch {
    return new Error('Unknown error')
  }
}

export function reportDatadogError(
  error: unknown,
  context: Record<string, unknown> = {},
) {
  if (!isRumInitialized || !error) return

  datadogRum.addError(toError(error), context)
}

function sanitizeRequestUrl(url?: string) {
  if (!url) return ''

  try {
    const parsed = /^https?:\/\//i.test(url)
      ? new URL(url)
      : new URL(url, globalThis.location?.origin ?? 'http://localhost')

    ;['token', 'code', 'senha', 'password', 'authorization'].forEach(
      (param) => {
        parsed.searchParams.delete(param)
      },
    )

    return `${parsed.pathname}${parsed.search}`
  } catch {
    return String(url).split('?')[0]
  }
}

function getHttpMethod(config: InternalAxiosRequestConfig) {
  return (config.method || 'get').toUpperCase()
}

function getHttpStatus(response?: AxiosResponse, error?: AxiosError): number {
  if (response?.status) return response.status
  if (error?.response?.status) return error.response.status
  return 0
}

function trackDatadogApiCall(
  config: DatadogRequestConfig,
  response?: AxiosResponse,
  error?: AxiosError,
) {
  if (!isRumInitialized || !config) return

  const url = sanitizeRequestUrl(config.url)
  const method = getHttpMethod(config)
  const status = getHttpStatus(response, error)
  const durationMs =
    config.metadata?.ddStartTime === undefined
      ? undefined
      : Date.now() - config.metadata.ddStartTime

  const context: Record<string, unknown> = {
    http: {
      method,
      url,
      status_code: status,
      ...(durationMs === undefined ? {} : { duration_ms: durationMs }),
    },
  }

  if (error) {
    context.error = {
      message: error.message || 'HTTP request failed',
    }
  }

  datadogRum.addAction(`api:${method}:${status || 'network'}`, context)

  const isServerOrNetworkFailure =
    Boolean(error) || status >= 500 || status === 0
  if (!isServerOrNetworkFailure) return

  const apiError =
    error instanceof Error
      ? error
      : new Error(`HTTP ${status} ${method} ${url}`)

  reportDatadogError(apiError, {
    source: 'http',
    ...context,
  })
}

export function installDatadogHttpTracking(axiosInstance: AxiosInstance) {
  if (
    !isRumInitialized ||
    !axiosInstance?.interceptors ||
    isHttpTrackingInstalled
  ) {
    return
  }

  isHttpTrackingInstalled = true

  axiosInstance.interceptors.request.use(
    (config: DatadogRequestConfig) => {
      config.metadata = {
        ...config.metadata,
        ddStartTime: Date.now(),
      }
      return config
    },
    (err) => Promise.reject(err),
  )

  axiosInstance.interceptors.response.use(
    (response) => {
      trackDatadogApiCall(response.config as DatadogRequestConfig, response)
      return response
    },
    (err: AxiosError) => {
      if (err.config) {
        trackDatadogApiCall(err.config, err.response, err)
      }
      return Promise.reject(err)
    },
  )
}

export function initializeDatadogRum(): boolean {
  if (globalThis.window === undefined) return false
  if (isRumInitialized) return true
  if (!isDatadogEnabled()) return false

  if (!datadogConfig.applicationId || !datadogConfig.clientToken) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[Datadog RUM] applicationId ou clientToken ausente no build.',
      )
    }
    return false
  }

  const initOptions: RumInitConfiguration = {
    applicationId: datadogConfig.applicationId,
    clientToken: datadogConfig.clientToken,
    site: datadogConfig.site,
    service: datadogConfig.service,
    env: datadogConfig.env,
    trackViewsManually: true,
    trackingConsent: 'granted',
    sessionSampleRate: 100,
    sessionReplaySampleRate: parseSampleRate(
      process.env.NEXT_PUBLIC_DD_SESSION_REPLAY_SAMPLE_RATE,
      20,
    ),
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: getAllowedTracingUrls(),
    traceSampleRate: parseSampleRate(
      process.env.NEXT_PUBLIC_DD_TRACE_SAMPLE_RATE,
      100,
    ),
  }

  if (datadogConfig.version) {
    initOptions.version = datadogConfig.version
  }

  datadogRum.init(initOptions)
  isRumInitialized = true
  return true
}

export function trackDatadogView(viewName: string) {
  if (!isRumInitialized) return
  datadogRum.startView({ name: viewName })
}

function DatadogViewTracker() {
  const pathname = usePathname()
  const lastView = useRef<string | null>(null)

  useEffect(() => {
    const viewName = pathname || 'unknown_route'
    if (lastView.current === viewName) return
    lastView.current = viewName
    trackDatadogView(viewName)
  }, [pathname])

  return null
}

type DatadogProviderProps = Readonly<{
  children: ReactNode
}>

export function DatadogProvider({ children }: DatadogProviderProps) {
  useEffect(() => {
    if (!initializeDatadogRum()) return

    import('@/services/base').then(({ apiClient }) => {
      installDatadogHttpTracking(apiClient)
    })
  }, [])

  return (
    <>
      <DatadogViewTracker />
      {children}
    </>
  )
}
